import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi/dist/zod-extensions";
import { z } from "zod";

extendZodWithOpenApi(z);

export const uploadBodySchema = z.object({
  useAcceleration: z.boolean().optional().openapi({
    description: "Use AWS S3 Acceleration",
    example: true,
  }),
  filename: z
    .string()
    .openapi({ description: "Filename to upload", example: "image.png" }),
  fileType: z.string().optional(),
  isRevisionFile: z.boolean().optional(),
});

export const uploadRequestSchema = z.object({
  body: uploadBodySchema,
});

export const uploadResponseSchema = z.object({
  url: z.string().openapi({ description: "URL to uploaded file" }),
  fields: z.object({
    "X-Amz-Signature": z.string().openapi({
      description: "AWS Signature",
      example: "testing123",
    }),
    "X-Amz-Credential": z.string().openapi({
      description: "AWS Credential",
      example: "testing123",
    }),
    "X-Amz-Algorithm": z.string().openapi({
      description: "AWS Algorithm",
      example: "testing123",
    }),
    "X-Amz-Date": z.string().openapi({
      description: "AWS Date",
      example: "testing123",
    }),
    Policy: z.string().openapi({
      description: "AWS Policy",
      example: "testing123",
    }),
    key: z.string().openapi({
      description: "AWS Key",
      example: "testing123",
    }),
    bucket: z.string().openapi({
      description: "AWS Bucket",
      example: "mg.assets",
    }),
  }),
});

// Multipart Upload Schemas
export const multipartInitiateBodySchema = z.object({
  filename: z
    .string()
    .openapi({ description: "Filename to upload", example: "large-video.mp4" }),
  fileSize: z.coerce.number().min(1).openapi({
    description: "Total file size in bytes",
    example: 1073741824,
  }),
  fileType: z.string().optional().openapi({
    description: "MIME type of the file",
    example: "video/mp4",
  }),
  isRevisionFile: z.boolean().optional().openapi({
    description: "Whether this is a revision file",
    example: false,
  }),
  useAcceleration: z.boolean().optional().openapi({
    description: "Use AWS S3 Acceleration",
    example: true,
  }),
  blockSize: z.coerce.number().min(1).optional().openapi({
    description: "Block size in bytes (default: 5MB)",
    example: 5242880,
  }),
});

export const multipartInitiateRequestSchema = z.object({
  body: multipartInitiateBodySchema,
});

export const multipartInitiateResponseSchema = z.object({
  uploadId: z.string().openapi({
    description: "S3 multipart upload ID",
    example: "abc123def456",
  }),
  key: z.string().openapi({
    description: "S3 object key",
    example: "puntt/misc/dev/large-video.mp4",
  }),
  bucket: z.string().openapi({
    description: "S3 bucket name",
    example: "puntt-videos",
  }),
  parts: z
    .array(
      z.object({
        partNumber: z.number().openapi({
          description: "Part number (1-based)",
          example: 1,
        }),
        uploadUrl: z.string().openapi({
          description: "Presigned URL for uploading this part",
          example: "https://bucket.s3.amazonaws.com/...",
        }),
      }),
    )
    .openapi({
      description: "Array of presigned URLs for each part",
    }),
});

export const multipartCompleteBodySchema = z.object({
  uploadId: z.string().openapi({
    description: "S3 multipart upload ID",
    example: "abc123def456",
  }),
  key: z.string().openapi({
    description: "S3 object key",
    example: "puntt/misc/dev/large-video.mp4",
  }),
  parts: z
    .array(
      z
        .object({
          partNumber: z.number().openapi({
            description: "Part number (1-based)",
            example: 1,
          }),
          etag: z.string().openapi({
            description: "ETag returned after uploading the part",
            example: '"d41d8cd98f00b204e9800998ecf8427e"',
          }),
        })
        .strict(),
    )
    .min(1)
    .openapi({
      description: "Array of completed parts with their ETags",
    }),
});

export const multipartCompleteRequestSchema = z.object({
  body: multipartCompleteBodySchema,
});

export const multipartCompleteResponseSchema = z.object({
  location: z.string().openapi({
    description: "Final S3 object location",
    example:
      "https://puntt-videos.s3.amazonaws.com/puntt/misc/dev/large-video.mp4",
  }),
  bucket: z.string().openapi({
    description: "S3 bucket name",
    example: "puntt-videos",
  }),
  key: z.string().openapi({
    description: "S3 object key",
    example: "puntt/misc/dev/large-video.mp4",
  }),
  etag: z.string().openapi({
    description: "ETag of the completed object",
    example: '"d41d8cd98f00b204e9800998ecf8427e-3"',
  }),
});

export type UploadRequest = z.infer<typeof uploadRequestSchema>;

export type UploadResponse = z.infer<typeof uploadResponseSchema>;

export type MultipartInitiateRequest = z.infer<
  typeof multipartInitiateRequestSchema
>;
export type MultipartInitiateResponse = z.infer<
  typeof multipartInitiateResponseSchema
>;
export type MultipartCompleteRequest = z.infer<
  typeof multipartCompleteRequestSchema
>;
export type MultipartCompleteResponse = z.infer<
  typeof multipartCompleteResponseSchema
>;
